import { CropRect, SegmentationData } from './types';

const flattenTransparency = (source: HTMLCanvasElement): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;
  const context = canvas.getContext('2d');
  if (!context) return source;
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(source, 0, 0);
  return canvas;
};

export const canvasToFile = (canvas: HTMLCanvasElement, originalName: string): Promise<File> => {
  const flattened = flattenTransparency(canvas);
  return new Promise((resolve, reject) => {
    flattened.toBlob(blob => {
      if (!blob) {
        reject(new Error('Failed to export canvas to blob'));
        return;
      }
      const baseName = originalName.replace(/\.[^.]+$/, '');
      const file = new File([blob], `${baseName}-edited.png`, { type: 'image/png' });
      resolve(file);
    }, 'image/png');
  });
};

export const applyCropToCanvas = (image: HTMLImageElement, crop: CropRect): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = crop.width;
  canvas.height = crop.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Failed to get canvas context');
  context.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
  return canvas;
};

export const applyMaskToCanvas = (image: HTMLImageElement, mask: ImageData): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Failed to get canvas context');

  context.drawImage(image, 0, 0);
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

  for (let index = 0; index < mask.data.length; index += 4) {
    if (mask.data[index] === 0) {
      imageData.data[index + 3] = 0;
    }
  }

  context.putImageData(imageData, 0, 0);
  return canvas;
};

export const createMaskFromRegions = (
  segmentation: SegmentationData,
  selectedIds: Set<number>,
  outputWidth: number,
  outputHeight: number
): ImageData => {
  const mask = new ImageData(outputWidth, outputHeight);
  const { regionMap, width: segW, height: segH } = segmentation;

  for (let y = 0; y < outputHeight; y++) {
    for (let x = 0; x < outputWidth; x++) {
      const segX = Math.min(Math.round((x * segW) / outputWidth), segW - 1);
      const segY = Math.min(Math.round((y * segH) / outputHeight), segH - 1);
      const regionId = regionMap[segY * segW + segX];
      const outIndex = (y * outputWidth + x) * 4;

      if (selectedIds.has(regionId)) {
        mask.data[outIndex] = 255;
        mask.data[outIndex + 1] = 255;
        mask.data[outIndex + 2] = 255;
      }
      mask.data[outIndex + 3] = 255;
    }
  }

  return mask;
};

export const cropToContent = (source: HTMLCanvasElement): HTMLCanvasElement => {
  const context = source.getContext('2d');
  if (!context) return source;
  const { width, height } = source;
  const imageData = context.getImageData(0, 0, width, height);
  const { data } = imageData;

  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha > 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX < minX || maxY < minY) return source;

  const cropW = maxX - minX + 1;
  const cropH = maxY - minY + 1;
  const cropped = document.createElement('canvas');
  cropped.width = cropW;
  cropped.height = cropH;
  const croppedContext = cropped.getContext('2d');
  if (!croppedContext) return source;
  croppedContext.drawImage(source, minX, minY, cropW, cropH, 0, 0, cropW, cropH);
  return cropped;
};

export const loadImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.addEventListener('load', () => {
      URL.revokeObjectURL(url);
      resolve(img);
    });
    img.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    });
    img.src = url;
  });
};
